import { memo } from 'react';
import { Heart, Zap, TrendingUp } from 'lucide-react';

/**
 * Barra de progresso animada com transições CSS independentes
 * Cada barra tem sua própria animação, permitindo movimentos simultâneos
 */
const AnimatedProgressBar = memo(({ 
  value, 
  max, 
  color = 'blue',
  height = 'h-2',
  showLabel = false,
  label = ''
}) => {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));
  
  const colorClasses = {
    blue: 'bg-blue-500',
    red: 'bg-gradient-to-r from-red-500 to-pink-600',
    yellow: 'bg-gradient-to-r from-yellow-400 to-orange-500',
    green: 'bg-gradient-to-r from-green-400 to-emerald-500',
    purple: 'bg-gradient-to-r from-purple-500 to-pink-500'
  };

  return (
    <div className="w-full">
      {showLabel && (
        <div className="flex justify-between text-sm mb-1">
          <span className="text-slate-600 font-nunito">{label}</span>
          <span className="text-slate-600 font-nunito">{value}/{max}</span>
        </div>
      )}
      <div className={`w-full bg-slate-100 rounded-full ${height} overflow-hidden`}>
        <div
          className={`${colorClasses[color] || colorClasses.blue} ${height} rounded-full`}
          style={{ 
            width: `${percentage}%`,
            transition: 'width 400ms cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        />
      </div>
    </div>
  );
});

AnimatedProgressBar.displayName = 'AnimatedProgressBar';

/**
 * Componente unificado para exibir todas as barras de status do Pokémon
 * Cada barra tem animação independente para atualizações simultâneas
 */
const PokemonStatBars = memo(({ 
  xp, 
  xpMax, 
  level, 
  hp, 
  maxHp, 
  energy, 
  maxEnergy 
}) => {
  return (
    <div className="space-y-2">
      {/* AURA Bar */}
      <div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-600 font-nunito">Nível {level}</span>
          <span className="text-slate-600 font-nunito">{xp}/{xpMax} AURA</span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
          <div
            data-testid="xp-bar"
            className="bg-gradient-to-r from-yellow-400 to-orange-500 h-2 rounded-full aura-shimmer"
            style={{ 
              width: `${(xp / xpMax) * 100}%`,
              transition: 'width 400ms cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          />
        </div>
      </div>

      {/* HP Bar */}
      <div className="flex items-center gap-2">
        <Heart className="w-4 h-4 text-red-500 flex-shrink-0" />
        <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
          <div
            data-testid="hp-bar"
            className="bg-gradient-to-r from-red-500 to-pink-600 h-2 rounded-full"
            style={{ 
              width: `${(hp / maxHp) * 100}%`,
              transition: 'width 400ms cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          />
        </div>
        <span className="text-sm text-slate-600 font-nunito w-16 text-right">{hp}/{maxHp}</span>
      </div>

      {/* Energy Bar */}
      <div className="flex items-center gap-2">
        <Zap className="w-4 h-4 text-blue-500 flex-shrink-0" />
        <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
          <div
            data-testid="energy-bar"
            className="bg-blue-500 h-2 rounded-full"
            style={{ 
              width: `${(energy / maxEnergy) * 100}%`,
              transition: 'width 400ms cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          />
        </div>
        <span className="text-sm text-slate-600 font-nunito w-16 text-right">{energy}/{maxEnergy}</span>
      </div>
    </div>
  );
});

PokemonStatBars.displayName = 'PokemonStatBars';

export { AnimatedProgressBar, PokemonStatBars };
export default PokemonStatBars;
